import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { SectionLoader, EmptyState } from "@/components/common";
import { Image as ImageIcon, PlayCircle, Plus, Trash2, EyeOff, Eye, Loader2, Upload } from "lucide-react";

const EMPTY = { type: "photo", title: "", description: "", url: "", data: "", event_id: "", is_published: true, r2_file_id: "" };

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    const [md, ev] = await Promise.all([
      api.get("/media", { params: { include_unpublished: true } }).then((r) => r.data).catch(() => []),
      api.get("/events/admin/all").then((r) => r.data).catch(() => []),
    ]);
    setItems(md); setEvents(ev); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Please choose an image under 15MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("prefix", "media");
      const res = await api.post("/storage/upload", fd);
      setForm((f) => ({ ...f, url: res.data.url, r2_file_id: res.data.file_id, data: "" }));
      toast.success("Uploaded to storage");
    } catch (err) {
      toast.error("Upload failed — is storage configured?");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (form.type === "photo" && !form.url) { toast.error("Please upload a photo"); return; }
    if (form.type === "video" && !form.url) { toast.error("Please provide a video embed URL"); return; }
    setSaving(true);
    const payload = { ...form, event_id: form.event_id || null };
    try { await api.post("/media", payload); toast.success("Media published"); setOpen(false); setForm(EMPTY); await load(); }
    catch (e) { toast.error("Could not save media"); } finally { setSaving(false); }
  };

  const takedown = async (m) => { try { await api.post(`/media/${m.media_id}/takedown`); toast.success("Media taken down"); await load(); } catch (e) { toast.error("Failed"); } };
  const restore = async (m) => { try { await api.post(`/media/${m.media_id}/restore`); toast.success("Media restored"); await load(); } catch (e) { toast.error("Failed"); } };
  const remove = async (m) => { try { await api.delete(`/media/${m.media_id}`); toast.success("Media deleted"); await load(); } catch (e) { toast.error("Failed"); } };
  const setPlacement = async (m, v) => {
    try { await api.post(`/media/${m.media_id}/placement`, { placement: v === "none" ? null : v }); toast.success(v === "none" ? "Removed from homepage" : "Homepage updated"); await load(); }
    catch (e) { toast.error("Could not update placement"); }
  };

  const filtered = items.filter((m) => filter === "all" || m.type === filter);

  if (loading) return <SectionLoader />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">Media Library</h1><p className="mt-1 text-muted-foreground">Upload photos & videos. Take down anything instantly.</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="admin-media-add-button"><Plus className="mr-2 h-4 w-4" />Add media</Button></DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
            <DialogHeader><DialogTitle className="font-serif text-xl">Add media</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, data: "", url: "", r2_file_id: "" })}>
                  <SelectTrigger className="mt-1.5" data-testid="admin-media-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="photo">Photo</SelectItem><SelectItem value="video">Video (embed URL)</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Title</Label><Input className="mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-media-title-input" /></div>
              {form.type === "photo" ? (
                <div>
                  <Label>Photo</Label>
                  <div className="mt-1.5 flex items-center gap-3">
                    <input id="mediafile" type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="admin-media-upload-input" />
                    <Button asChild variant="secondary" disabled={uploading}><label htmlFor="mediafile" className="cursor-pointer">{uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}{uploading ? "Uploading…" : "Choose image"}</label></Button>
                    {form.url && <img src={form.url} alt="preview" className="h-12 w-12 rounded-md object-cover" />}
                  </div>
                  <p className="mt-1.5 text-xs text-muted-foreground">Stored on Cloudflare R2.</p>
                </div>
              ) : (
                <div><Label>Video embed URL</Label><Input className="mt-1.5" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://www.youtube.com/embed/..." data-testid="admin-media-url-input" /><p className="mt-1 text-xs text-muted-foreground">Use the embed URL (e.g. youtube.com/embed/ID).</p></div>
              )}
              <div><Label>Link to event (optional)</Label>
                <Select value={form.event_id || "none"} onValueChange={(v) => setForm({ ...form, event_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="No event" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">No event</SelectItem>{events.map((e) => <SelectItem key={e.event_id} value={e.event_id}>{e.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Description</Label><Textarea className="mt-1.5" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            </div>
            <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="admin-media-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={filter} onValueChange={setFilter}><TabsList><TabsTrigger value="all">All</TabsTrigger><TabsTrigger value="photo">Photos</TabsTrigger><TabsTrigger value="video">Videos</TabsTrigger></TabsList></Tabs>

      {filtered.length === 0 ? (
        <EmptyState icon={ImageIcon} title="No media yet" description="Upload photos and videos from your events." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((m) => (
            <Card key={m.media_id} className={`overflow-hidden border-border bg-card ${!m.is_published ? "opacity-70" : ""}`} data-testid="admin-media-card">
              <AspectRatio ratio={16 / 9} className="bg-secondary">
                {m.type === "video" ? (
                  m.url ? <iframe title={m.title} src={m.url} className="h-full w-full" allowFullScreen /> : <div className="flex h-full items-center justify-center"><PlayCircle className="h-10 w-10 text-muted-foreground" /></div>
                ) : (
                  <img src={m.data || m.url} alt={m.title || ""} className="h-full w-full object-cover" />
                )}
              </AspectRatio>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <p className="truncate font-medium text-foreground">{m.title || "Untitled"}</p>
                  {m.is_published ? <Badge variant="secondary">Live</Badge> : <Badge variant="outline">Taken down</Badge>}
                </div>
                {m.type === "photo" && (
                  <div className="mt-3">
                    <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Homepage</Label>
                    <Select value={m.placement || "none"} onValueChange={(v) => setPlacement(m, v)}>
                      <SelectTrigger className="mt-1 h-8 text-xs" data-testid={`admin-media-placement-${m.media_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not on homepage</SelectItem>
                        <SelectItem value="hero">Hero photo (top of page)</SelectItem>
                        <SelectItem value="first_friday">“Your first Friday” photo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="mt-3 flex gap-1">
                  {m.is_published ? (
                    <Button size="sm" variant="ghost" onClick={() => takedown(m)} data-testid="admin-media-takedown-button"><EyeOff className="mr-1.5 h-3.5 w-3.5" />Take down</Button>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => restore(m)} data-testid="admin-media-restore-button"><Eye className="mr-1.5 h-3.5 w-3.5" />Restore</Button>
                  )}
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="text-destructive"><Trash2 className="mr-1.5 h-3.5 w-3.5" />Delete</Button></AlertDialogTrigger>
                    <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete this media?</AlertDialogTitle><AlertDialogDescription>This permanently removes the file. This cannot be undone.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(m)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
